import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { projectApi } from "../../backend/client";
import { useCurrentUser } from "../../backend/queries/auth";
import {
  ProjectStatusBadge,
  ProjectTypeBadge,
} from "../../components/domain-badges";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage] = useState(5);
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const user = currentUser?.profile || currentUser || null;

  useEffect(() => {
    if (!user?.id) return;

    const fetchProjects = async () => {
      const { data, error } = await projectApi.list();

      if (error) {
        console.error("Error fetching projects:", error);
      } else {
        setProjects(data || []);
      }
    };

    fetchProjects();
  }, [user?.id]);

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = projects.slice(
    indexOfFirstProject,
    indexOfLastProject,
  );
  const totalPages = Math.max(1, Math.ceil(projects.length / projectsPerPage));

  const handleViewProject = (id) => {
    navigate(`/my-projects/${id}`);
  };

  return (
    <div className="flex flex-col">
      <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-5">
          <div>
            <h1 className="text-2xl font-semibold">My projects</h1>
            <p className="text-sm text-muted-foreground">
              Track your relocation projects and open one for details.
            </p>
          </div>

          <Card>
            <CardHeader className="p-4 sm:p-5">
              <CardTitle>Project list</CardTitle>
              <CardDescription>
                {projects.length} projects assigned to you
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
              <div className="space-y-3 md:hidden">
                {currentProjects.length === 0 ? (
                  <EmptyProjects />
                ) : (
                  currentProjects.map((project) => (
                    <div
                      key={project.id}
                      className="rounded-md border bg-card p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs text-muted-foreground">
                            {formatDate(project.project_start_date)}
                          </div>
                          <div className="mt-1 text-sm font-semibold">
                            {project.relocating_from || "N/A"} to{" "}
                            {project.relocating_to || "N/A"}
                          </div>
                        </div>
                        <ProjectStatusBadge status={project.status} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <ProjectTypeBadge type={project.project_type} />
                        <Badge variant="outline" className="rounded-md">
                          {project.agent
                            ? `${project.agent.firstname} ${project.agent.lastname}`
                            : "No agent assigned"}
                        </Badge>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewProject(project.id)}
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="hidden rounded-md border md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Start date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentProjects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <EmptyProjects />
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentProjects.map((project) => (
                        <TableRow key={project.id}>
                          <TableCell>
                            {formatDate(project.project_start_date)}
                          </TableCell>
                          <TableCell>
                            <ProjectStatusBadge status={project.status} />
                          </TableCell>
                          <TableCell>
                            {project.agent
                              ? `${project.agent.firstname} ${project.agent.lastname}`
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            <ProjectTypeBadge type={project.project_type} />
                          </TableCell>
                          <TableCell>
                            {project.relocating_from || "N/A"}
                          </TableCell>
                          <TableCell>
                            {project.relocating_to || "N/A"}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="View project"
                                onClick={() => handleViewProject(project.id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const formatDate = (dateString) =>
  dateString ? new Date(dateString).toLocaleDateString() : "N/A";

const EmptyProjects = () => (
  <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
    No projects available.
  </div>
);

export default MyProjects;
